<template>
  <header id="main-header">
    <!-- Menu Left -->
    <div class="menu-left">
      <router-link to="/apps">
        <img src="@/assets/images/logo.png" class="logo" alt="Macha Logo" />
      </router-link>

      <div v-if="!lodash.isEmpty(selectedApp)" class="selected-app">
        <img
          :src="getAppIconByName(selectedApp.appName)"
          :alt="selectedApp.appName"
        />
        <span>{{ selectedApp.appName }}</span>
      </div>
    </div>

    <!--START: Menu Right -->
    <div class="menu-right">
      <div
        v-if="selectedCompany && user.companies.length > 1"
        class="companies-selection-wrapper"
      >
        <!--START: Selected company-->
        <div class="selected-company company-item" @click="toggleCompaniesMenu">
          <!--START: Company info-->
          <div class="company-info">
            <h3>{{ selectedCompany.name }}</h3>
            <span>
              {{
                selectedCompany.integrations.zendesk.credentials.subdomain
              }}.zendesk.com
            </span>
          </div>

          <unicon v-if="user.companies.length > 1" name="angle-down"></unicon>
          <!--END: Company Info-->
        </div>
        <!--END: Selected company-->
        <!--START: Company selection-->
        <div
          v-if="showCompaniesMenu && user.companies.length > 1"
          class="companies-wrapper"
          v-click-outside="toggleCompaniesMenu"
        >
          <div v-for="company in user.companies" :key="company.id">
            <div
              v-if="selectedCompany._id !== company.companyId"
              class="company-item"
              @click="toggleCompany(company._id)"
            >
              <!--START: Company Info-->
              <div class="company-info">
                <h3>{{ company.name }}</h3>
                <span>
                  {{
                    company.integrations.zendesk.credentials.subdomain
                  }}.zendesk.com
                </span>
              </div>
              <!--END: Company Info-->
            </div>
          </div>
        </div>
        <!--END: Company selection-->
      </div>

      <!-- Apps -->
      <div class="btn-apps-wrapper">
        <div class="menu-item" @click="toggleAppsMenu">
          <unicon name="apps"></unicon>
          <span>Apps</span>
        </div>

        <!-- Apps Menu -->
        <div
          v-if="showAppsMenu"
          class="apps-menu"
          v-click-outside="toggleAppsMenu"
        >
          <div class="apps-list">
            <div
              v-for="app in apps"
              :key="app.key"
              class="app"
              :class="{ 'not-downloaded': !app.hasDownloaded }"
              @click="gotoApp(app)"
            >
              <img :src="getAppIcon(app.appIcon)" :alt="app.title" />
              <span class="title">{{ app.title }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="menu-item" @click="showAdminSettings">
        <unicon name="setting"></unicon>
      </div>

      <!--START: User Button-->
      <div class="btn-user-wrapper">
        <div class="menu-item" @click="toggleUserMenu">
          <unicon name="user"></unicon>
        </div>
        <div
          v-if="showUserMenu"
          class="user-menu"
          v-click-outside="toggleUserMenu"
        >
          <router-link to="/logout" class="menu-item">
            <span>Logout</span>
          </router-link>
        </div>
      </div>
      <!--END: User Button-->
    </div>
    <!--END: Menu Right -->

    <!--START: Loader-->
    <LineLoader :show="showOptions.lineLoader"></LineLoader>
    <!--END: Loader-->
  </header>
</template>

<script>
// Libraries
import _ from "lodash";

// Importing components
import LineLoader from "@/components/loaders/LineLoader";

// Services
import { TranslationsAppService } from "@/services";

//Importing libraries
import vClickOutside from "v-click-outside";

export default {
  name: "Header",
  data() {
    return {
      lodash: _,
      showUserMenu: false,
      showAppsMenu: false,
      showCompaniesMenu: false,
      showOptions: {
        lineLoader: false,
      },
      apps: [
        {
          key: "ticketTranslations",
          title: "Ticket Translations AI",
          route: "translations",
          zendeskAppId: "1166071",
          appIcon: "ticket-translations.png",
          hasDownloaded: false,
          app: {},
        },
      ],
    };
  },
  computed: {
    user() {
      return this.$store.getters.user.user;
    },
    company() {
      return this.$store.getters.company;
    },
    selectedCompany() {
      let selectedCompany = this.user?.companies.find(
        (c) => c._id === this.company._id
      );

      return selectedCompany;
    },
    selectedApp() {
      return this.$store.getters.selectedApp;
    },
    appsSuite() {
      return this.$store.getters.appsSuite;
    },
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  components: { LineLoader },
  async created() {
    this.getAppsSuite();
  },
  methods: {
    async getAppsSuite() {
      if (this.appsSuite.length === 0) {
        const response = { ticketTranslations: {} };
        const data = { companyId: this.company._id };

        response.ticketTranslations =
          await TranslationsAppService.CompanyService.GetCompany(data);

        for (const [key, app] of Object.entries(response)) {
          if (app?.data?.companyId) {
            const selectedApp = this.apps.find((a) => a.key === key);
            selectedApp.app = app.data;
            selectedApp.hasDownloaded = true;
          }
        }

        this.$store.commit("updateAppsSuite", this.apps);
      }

      this.apps = this.appsSuite;
    },

    // Get app icon
    getAppIcon(icon) {
      let iconURL = `/assets/images/apps/${icon}`;
      return iconURL;
    },

    // Get icon by app name
    getAppIconByName(appName) {
      let icon = "";
      switch (appName) {
        case "AI Ticket Translations":
          icon = "ticket-translations.png";
          break;
      }

      let iconURL = `/assets/images/apps/${icon}`;
      return iconURL;
    },

    // Get the Zendesk marketplace app link
    getZendeskMarketplaceLink(appId) {
      return `https://www.zendesk.com/in/marketplace/apps/support/${appId}`;
    },

    // Click action
    gotoApp(app) {
      this.$store.commit("updateSelectedApp", {});
      if (app.hasDownloaded) {
        this.$router.push({ path: `/apps/${app.route}` });
      } else {
        window
          .open(this.getZendeskMarketplaceLink(app.zendeskAppId), "_blank")
          .focus();
      }
    },

    toggleCompany(companyId) {
      this.$store.commit("updateSelectedApp", {});
      this.$store.commit("updateAppsSuite", []);
      const data = {
        email: this.user?.email,
        companyId,
      };
      this.$emit("toggleCompany", data);
    },

    showAdminSettings() {
      this.$emit("showAdminSettings");
    },

    toggleAppsMenu() {
      this.showAppsMenu = !this.showAppsMenu;
    },

    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu;
    },

    toggleCompaniesMenu() {
      this.showCompaniesMenu = !this.showCompaniesMenu;
    },
  },
};
</script>

<style scoped lang="scss">
header {
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0.75rem calc(2rem);
  width: calc(100% - 4rem);
  z-index: 15;
  background-color: $headerBackgroundColor;
  border-bottom: 1px solid $headerBorderColor;
  backdrop-filter: blur(15px);
}

.menu-left {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1.5rem;
  flex: 1;
}

.logo {
  display: block;
  width: 6rem;
  transform: translateY(-0.15rem);
}

.selected-app {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.55rem;

  img {
    width: 1.5rem;
    border-radius: 0.35rem;
  }

  span {
    font-size: $smallerFontSize;
    color: $greyColor;
    font-weight: $mediumFontWeight;
    margin-right: 0.5rem;
  }
}

.menu-item {
  @include transition;
  display: flex;
  flex-direction: row;
  align-items: center;
  color: $greyColor;
  font-size: $smallerFontSize;
  padding: 0.35rem 0.75rem;
  border-radius: 0.5rem;
  margin: 0 0.5rem;
  gap: 0.5rem;
  cursor: pointer;

  ::v-deep svg {
    fill: $greyColor;
    width: 1rem;
  }

  &:hover {
    color: $greyColor;
    text-decoration: none;

    ::v-deep svg {
      fill: $greyColor;
    }
  }

  &.router-link-active {
    color: $whiteColor;
    background-color: $purpleColor;

    ::v-deep svg {
      fill: $whiteColor;
    }
  }

  &.task-list-item {
    .unicon {
      transform: translateY(2px);
    }
  }
}

.menu-right {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  gap: 0.5rem;

  .menu-item {
    margin: 0;
  }

  .router-link-active {
    font-weight: $mediumFontWeight;

    ::v-deep svg {
    }
  }
}

.btn-apps-wrapper,
.btn-user-wrapper {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;

  .menu-item {
    margin: 0;
  }

  .apps-menu,
  .user-menu {
    background-color: $whiteColor;
    box-shadow: 0 0 0.7rem -0.1rem $boxShadowColor;
    position: absolute;
    padding: 0.5rem;
    border-radius: 0.75rem;
    top: calc(100% + 0.25rem);
    white-space: nowrap;
    right: 0;
    z-index: 100;

    .menu-item {
      margin: 0;

      &:hover {
        background: darken($whiteColor, 5%);
      }
    }
  }
}

.btn-apps-wrapper {
  > .menu-item {
    border: 1px solid lighten($buttonBorderColor, 15%);
    border-radius: 0.5rem;
    margin-left: 1rem;

    ::v-deep svg {
      margin-right: 0;
    }

    &:hover {
      border: 1px solid lighten($buttonBorderColor, 5%);
      background: darken($whiteColor, 2.5%);

      ::v-deep svg {
        fill: $purpleColor;
      }
    }
  }
}

.account-integrated {
  display: inline-block;
  width: max-content;
  background-color: $greenMessageColor;
  border: 1px solid $greenMessageBorderColor;
  color: $greenMessageTextColor;
  font-weight: $mediumFontWeight;
  font-size: $smallerFontSize;
  padding: 0.25rem 0.75rem;
  border-radius: 0.5rem;

  .unicon svg {
    display: inline-block;
    vertical-align: middle;
    fill: $greenMessageTextColor;
    height: auto;
    width: 0.75rem;
    margin-right: 0.5rem;
  }

  span {
    display: inline-block;
    vertical-align: middle;
  }
}

.selected-company {
  position: relative;
  margin-bottom: 0;
  border-radius: 0.5rem;
  background-color: lighten($lightGreyColor, 35%);
  border: 1px solid lighten($lightGreyColor, 25%);
  margin-bottom: 0;
  min-height: 1.75rem;

  ::v-deep .unicon {
    position: absolute;
    top: 50%;
    right: 0.35rem;
    transform: translateY(-50%);
    svg {
      height: auto;
      width: 1rem;
      margin-top: 0.25rem;
    }
  }
}

.company-item {
  @include transition;
  display: flex;
  flex-direction: row;
  align-items: center;

  .company-info {
    flex: 1;
    text-align: left;
    max-width: 10rem;
    margin-right: 0.5rem;
    padding: 0.25rem 0.75rem;

    h3 {
      font-size: $smallerFontSize;
      white-space: nowrap;
      margin-bottom: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    span {
      display: block;
      color: $greyColor;
      font-size: $smallestFontSize;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-right: 1rem;

      &.no-store {
        opacity: $lightOpacity;
      }
    }
  }

  &:hover {
    cursor: pointer;
    background: darken($whiteColor, 5%);
  }
}

.companies-selection-wrapper {
  position: relative;

  .companies-wrapper {
    position: absolute;
    background-color: $whiteColor;
    box-shadow: 0 0 0.7rem -0.1rem $boxShadowColor;
    border-radius: 0.75rem;
    top: calc(100% + 0.25rem);
    left: 0;
    max-height: 35vh;
    width: max-content;
    overflow: scroll;

    .company-item {
      border: none;
      padding: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

::v-deep .line-loader {
  left: 0;
  bottom: -5px;
}

.apps-menu-item {
  position: relative;

  .apps-list {
    position: absolute;
    top: 100%;
    right: 0;
  }
}

.apps-list {
  white-space: normal;
  width: 20rem;
}

.app {
  display: inline-block;
  width: calc(33% - 1rem);
  margin: 0.5rem;
  vertical-align: top;
  text-align: center;
  cursor: pointer;

  &.not-downloaded {
    opacity: 0.5;
  }

  img {
    display: block;
    width: 3rem;
    margin: 0 auto 0.5rem;
    border-radius: 0.5rem;
    border: 1px solid #ececec;
    transform: translateY(0);
    transition: all 0.3s;
  }

  .title {
    display: block;
    font-size: $smallestFontSize;
    font-weight: $mediumFontWeight;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}
</style>
